import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import {
  TravellerAttributeTemplateData,
  createTravellerAttributeTemplate,
  deleteTravellerAttributeTemplate,
  duplicateTravellerAttributeTemplate,
  loadCompanyTravellerAttributeTemplate,
  loadCompanyTravellerAttributeTemplateVariables,
  loadCompanyTravellerAttributeTemplates,
  updateTravellerAttributeTemplate,
} from './requests';

export const useCompanyTravellerAttributeTemplates = ({
  queryData,
  systemAttributes,
  ...rest
}: {
  queryData?: object;
  systemAttributes?: boolean;
  staleTime?: number;
  keepPreviousData?: boolean;
} = {}) => {
  const queryString = toQueryString(queryData ?? {});
  return useQuery({
    keepPreviousData: true,
    staleTime: 1000,
    queryKey: ['traveller-attributes', queryString, systemAttributes],
    queryFn: () => loadCompanyTravellerAttributeTemplates(queryString, systemAttributes),
    ...rest,
  });
};

export const useCompanyTravellerAttributeTemplate = (
  shortId: string,
  isSystemAttributeQuery?: boolean
) => {
  return useQuery({
    queryKey: ['traveller-attributes', shortId],
    queryFn: () => loadCompanyTravellerAttributeTemplate(shortId, isSystemAttributeQuery),
    enabled: Boolean(shortId),
  });
};

export const useCompanyTravellerAttributeTemplateVariables = () => {
  return useQuery({
    queryKey: ['traveller-attribute-variables'],
    queryFn: () => loadCompanyTravellerAttributeTemplateVariables(),
  });
};

export const useCreateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTravellerAttributeTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeTemplateCreated'));
    },
  });
};

export const useUpdateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, shortId }: { data: TravellerAttributeTemplateData; shortId: string }) =>
      updateTravellerAttributeTemplate(shortId, data),
    onSuccess: ({ shortId }) => {
      queryClient.removeQueries(['traveller-attributes', shortId]);
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeEdited'));
    },
  });
};

export const useDuplicateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => duplicateTravellerAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeTemplateDuplicated'));
    },
  });
};

export const useDeleteTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => deleteTravellerAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeDeleted'));
    },
  });
};

export const locale = {
  en: {
    attributeTemplateCreated: 'Field created',
    attributeTemplateDuplicated: 'Field duplicated',
    attributeEdited: 'Field edited',
    attributeDeleted: 'Field deleted',
  },
};
